// Lightweight replacements for `window.confirm`, `window.alert`, and
// `window.prompt`. Native dialogs are silently suppressed in some managed
// browsers / webviews (school Wi-Fi, embedded frames, certain PWAs), which
// caused destructive buttons in this app to look "dead". These helpers
// always render a real in-app modal via direct DOM APIs so they work from
// any context (async handler, `onClick`, keydown, etc.).
//
// All helpers return Promises. Callers should `await` them from an async
// handler:
//
//   if (!(await confirmDialog("Delete this record?"))) return;
//   await alertDialog("Saved!");
//   const note = await promptDialog("Why?", "");

const OVERLAY_STYLE =
  "position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:99999;" +
  "display:flex;align-items:center;justify-content:center;padding:16px;" +
  "backdrop-filter:blur(2px);";

const CARD_STYLE =
  "background:#ffffff;border-radius:14px;max-width:440px;width:100%;" +
  "padding:22px 24px;box-shadow:0 24px 60px rgba(15,23,42,0.35);" +
  "font-family:inherit;color:#0f172a;";

const MSG_STYLE =
  "white-space:pre-wrap;font-size:15px;line-height:1.55;margin-bottom:18px;";

const ACTIONS_STYLE = "display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;";

function makeButton(label, { primary = false, danger = false, testid } = {}) {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = label;
  const bg = danger ? "#dc2626" : primary ? "#0f172a" : "#ffffff";
  const color = danger || primary ? "#ffffff" : "#0f172a";
  const border = danger || primary ? "0" : "1px solid #d1d5db";
  btn.style.cssText =
    `padding:9px 18px;border-radius:9px;border:${border};background:${bg};` +
    `color:${color};cursor:pointer;font-size:14px;font-weight:600;` +
    `transition:opacity 120ms ease;`;
  btn.onmouseenter = () => (btn.style.opacity = "0.85");
  btn.onmouseleave = () => (btn.style.opacity = "1");
  if (testid) btn.setAttribute("data-testid", testid);
  return btn;
}

function mountModal({ message, buildActions, onDismiss }) {
  const overlay = document.createElement("div");
  overlay.style.cssText = OVERLAY_STYLE;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");

  const card = document.createElement("div");
  card.style.cssText = CARD_STYLE;

  const msg = document.createElement("div");
  msg.style.cssText = MSG_STYLE;
  msg.textContent = String(message ?? "");

  const actions = document.createElement("div");
  actions.style.cssText = ACTIONS_STYLE;

  card.append(msg);
  const { extraNode, buttons } = buildActions();
  if (extraNode) card.appendChild(extraNode);
  buttons.forEach((b) => actions.appendChild(b));
  card.append(actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const cleanup = () => {
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onDismiss();
    }
  };
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) onDismiss();
  });
  document.addEventListener("keydown", onKey);

  return { cleanup, card };
}

export function confirmDialog(
  message,
  { confirmLabel = "Confirm", cancelLabel = "Cancel", danger = false } = {}
) {
  return new Promise((resolve) => {
    let resolved = false;
    const finish = (v) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(v);
    };
    const { cleanup } = mountModal({
      message,
      onDismiss: () => finish(false),
      buildActions: () => {
        const cancel = makeButton(cancelLabel, { testid: "dialog-cancel" });
        const ok = makeButton(confirmLabel, {
          primary: !danger,
          danger,
          testid: "dialog-confirm",
        });
        cancel.onclick = () => finish(false);
        ok.onclick = () => finish(true);
        setTimeout(() => ok.focus(), 20);
        return { buttons: [cancel, ok] };
      },
    });
  });
}

export function alertDialog(message, { confirmLabel = "OK" } = {}) {
  return new Promise((resolve) => {
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };
    const { cleanup } = mountModal({
      message,
      onDismiss: finish,
      buildActions: () => {
        const ok = makeButton(confirmLabel, { primary: true, testid: "dialog-ok" });
        ok.onclick = finish;
        setTimeout(() => ok.focus(), 20);
        return { buttons: [ok] };
      },
    });
  });
}

export function promptDialog(
  message,
  defaultValue = "",
  { confirmLabel = "Save", cancelLabel = "Cancel", placeholder = "" } = {}
) {
  return new Promise((resolve) => {
    let resolved = false;
    let input;
    const finish = (v) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(v);
    };
    const { cleanup } = mountModal({
      message,
      onDismiss: () => finish(null),
      buildActions: () => {
        input = document.createElement("input");
        input.type = "text";
        input.value = defaultValue ?? "";
        input.placeholder = placeholder;
        input.setAttribute("data-testid", "dialog-input");
        input.style.cssText =
          "width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px;" +
          "font-size:14px;margin-bottom:16px;box-sizing:border-box;";
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            finish(input.value);
          }
        });
        const cancel = makeButton(cancelLabel, { testid: "dialog-cancel" });
        const ok = makeButton(confirmLabel, { primary: true, testid: "dialog-confirm" });
        cancel.onclick = () => finish(null);
        ok.onclick = () => finish(input.value);
        setTimeout(() => input.focus(), 20);
        return { extraNode: input, buttons: [cancel, ok] };
      },
    });
  });
}

// Convenience shim so we can drop-in replace the tiny subset of callers
// that used `alert()` purely to surface non-blocking info. Prefers sonner
// if it's already available on window (some pages do), else falls back to
// the modal alert.
export function toastError(message) {
  // Keep it simple: reuse the modal alert. This is called from failure
  // paths only, so an in-app modal is acceptable UX.
  return alertDialog(message);
}
